import api from "../../auth/apiClient";
import type { Application, Menu, Role, User, UserMenuOverride, SuccessMessage, PaginatedParams } from "../../rbac/types";

// RBAC API service (uses centralized authenticated client)
export const rbacService = {
  // Applications
  createApplication(input: { app_name: string }) {
    return api.post<Application>(`/rbac/applications`, input).then(r => r.data);
  },
  getApplications(params?: PaginatedParams) {
    return api.get<Application[]>(`/rbac/applications`, { params }).then(r => r.data);
  },
  getApplicationById(appId: string) {
    return api.get<Application>(`/rbac/applications/${appId}`).then(r => r.data);
  },
  updateApplication(appId: string, input: { app_name: string }) {
    return api.put<Application>(`/rbac/applications/${appId}`, input).then(r => r.data);
  },
  deleteApplication(appId: string) {
    return api.delete<SuccessMessage>(`/rbac/applications/${appId}`).then(r => r.data);
  },

  // Menus
  createMenu(input: { app_id: string; menu_key: string; display_name: string; path: string; }) {
    return api.post<Menu>(`/rbac/menus`, input).then(r => r.data);
  },
  getMenus(params?: PaginatedParams) {
    return api.get<Menu[]>(`/rbac/menus`, { params }).then(r => r.data);
  },
  getMenuById(menuId: string) {
    return api.get<Menu>(`/rbac/menus/${menuId}`).then(r => r.data);
  },
  updateMenu(menuId: string, input: Partial<{ app_id: string; menu_key: string; display_name: string; path: string; }>) {
    return api.put<Menu>(`/rbac/menus/${menuId}`, input).then(r => r.data);
  },
  deleteMenu(menuId: string) {
    return api.delete<SuccessMessage>(`/rbac/menus/${menuId}`).then(r => r.data);
  },

  // Roles
  createRole(input: { role_name: string; description?: string; app_id?: string | null; }) {
    return api.post<Role>(`/rbac/roles`, input).then(r => r.data);
  },
  getRoles(params?: PaginatedParams) {
    return api.get<Role[]>(`/rbac/roles`, { params }).then(r => r.data);
  },
  getRoleById(roleId: string) {
    return api.get<Role>(`/rbac/roles/${roleId}`).then(r => r.data);
  },
  updateRole(roleId: string, input: Partial<{ role_name: string; description: string; app_id: string | null }>) {
    return api.put<Role>(`/rbac/roles/${roleId}`, input).then(r => r.data);
  },
  deleteRole(roleId: string) {
    return api.delete<SuccessMessage>(`/rbac/roles/${roleId}`).then(r => r.data);
  },

  // Users
  createUser(input: { email: string; full_name?: string }) {
    return api.post<User>(`/rbac/users`, input).then(r => r.data);
  },
  getUsers(params?: PaginatedParams) {
    return api.get<User[]>(`/rbac/users`, { params }).then(r => r.data);
  },
  getUserById(userId: string) {
    return api.get<User>(`/rbac/users/${userId}`).then(r => r.data);
  },
  updateUser(userId: string, input: { full_name?: string }) {
    return api.put<User>(`/rbac/users/${userId}`, input).then(r => r.data);
  },

  // User-Role relationships
  assignRoleToUser(userId: string, roleId: string) {
    return api.post<SuccessMessage>(`/rbac/users/${userId}/roles/${roleId}`).then(r => r.data);
  },
  removeRoleFromUser(userId: string, roleId: string) {
    return api.delete<SuccessMessage>(`/rbac/users/${userId}/roles/${roleId}`).then(r => r.data);
  },
  getUserRoles(userId: string) {
    return api.get<Role[]>(`/rbac/users/${userId}/roles`).then(r => r.data);
  },

  // Role-Menu relationships
  assignMenuToRole(roleId: string, menuId: string) {
    return api.post<SuccessMessage>(`/rbac/roles/${roleId}/menus/${menuId}`).then(r => r.data);
  },
  removeMenuFromRole(roleId: string, menuId: string) {
    return api.delete<SuccessMessage>(`/rbac/roles/${roleId}/menus/${menuId}`).then(r => r.data);
  },
  getRoleMenus(roleId: string) {
    return api.get<Menu[]>(`/rbac/roles/${roleId}/menus`).then(r => r.data);
  },

  // User menu overrides
  createOrUpdateOverride(userId: string, input: { menu_id: string; has_access: boolean; }) {
    return api.post<UserMenuOverride>(`/rbac/users/${userId}/overrides`, input).then(r => r.data);
  },
  getUserOverrides(userId: string) {
    return api.get<UserMenuOverride[]>(`/rbac/users/${userId}/overrides`).then(r => r.data);
  },
  deleteUserOverride(userId: string, menuId: string) {
    return api.delete<SuccessMessage>(`/rbac/users/${userId}/overrides/${menuId}`).then(r => r.data);
  },
};

export default rbacService;
